import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, Loader2, BookMarked } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function Unsubscribe() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const email = params.get("email") || "";
    const token = params.get("token") || "";
    const [state, setState] = useState("loading"); // loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (!email || !token) {
            setState("error");
            setErrorMsg(t("unsubscribe.invalid_link"));
            return;
        }
        let alive = true;
        (async () => {
            try {
                await api.post("/users/unsubscribe", { email, token });
                if (alive) setState("success");
            } catch (err) {
                if (alive) {
                    setErrorMsg(formatApiError(err.response?.data?.detail));
                    setState("error");
                }
            }
        })();
        return () => { alive = false; };
    }, [email, token, t]);

    return (
        <main
            data-testid="unsubscribe-page"
            className="min-h-[calc(100vh-73px)] flex items-center justify-center px-5 py-16"
        >
            <section className="max-w-md text-center">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[var(--ink)] mb-10"
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                {state === "loading" && (
                    <div data-testid="unsubscribe-loading">
                        <Loader2 size={28} strokeWidth={1.5} className="animate-spin text-[var(--accent)] mb-4 mx-auto" />
                        <p className="text-[var(--ink-muted)] text-sm">{t("unsubscribe.loading")}</p>
                    </div>
                )}

                {state === "success" && (
                    <div data-testid="unsubscribe-success">
                        <CheckCircle2 size={32} strokeWidth={1.5} className="text-[var(--accent)] mb-4 mx-auto" />
                        <h1
                            className="font-display text-3xl text-[var(--ink)] mb-3"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                        >
                            {t("unsubscribe.success_title")}
                        </h1>
                        <p className="text-[var(--ink-muted)] text-sm leading-relaxed mb-8">
                            {t("unsubscribe.success_lead", { email })}
                        </p>
                        <Link
                            to="/"
                            className="inline-block text-sm uppercase tracking-widest text-[var(--accent)] hover:opacity-80 border border-[var(--accent)] rounded-full px-6 py-2 transition-opacity"
                            data-testid="unsubscribe-home-link"
                        >
                            {t("unsubscribe.back_home")}
                        </Link>
                    </div>
                )}

                {state === "error" && (
                    <div data-testid="unsubscribe-error">
                        <AlertTriangle size={32} strokeWidth={1.5} className="text-[var(--danger)] mb-4 mx-auto" />
                        <h1 className="font-display text-3xl text-[var(--ink)] mb-3">
                            {t("unsubscribe.error_title")}
                        </h1>
                        <p className="text-[var(--ink-muted)] text-sm leading-relaxed mb-8">
                            {errorMsg || t("unsubscribe.error_lead")}
                        </p>
                        <Link
                            to="/contacto"
                            className="inline-block text-sm uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)]"
                            data-testid="unsubscribe-contact-link"
                        >
                            {t("unsubscribe.contact_us")}
                        </Link>
                    </div>
                )}
            </section>
        </main>
    );
}
